import React, { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { formatUnits, parseUnits } from 'viem';
import { Gift, Loader2, CheckCircle, AlertCircle, ExternalLink, Wallet, TrendingUp, Award, Clock, Coins, DollarSign, Timer, Trophy, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { formatNumber, formatAddress } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TIP_TOKEN_ADDRESS = process.env.REACT_APP_TIP_TOKEN_ADDRESS || '0x45D8e90deFB7a88C3e5584043205da5a90a43A34';
const TREASURY_ADDRESS = process.env.REACT_APP_TREASURY_ADDRESS || '0xcAD86262Df9fe4333cD923f7321757E910A0447F';

// Scoring weight constants (must match backend scoring_engine.py)
const TIPPER_WEIGHT = 0.75;
const CREATOR_WEIGHT = 0.25;

// Countdown Timer Component
const CountdownTimer = ({ targetDate, label, variant = 'default' }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const target = new Date(targetDate).getTime();
      const now = new Date().getTime();
      const difference = target - now;
      
      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };
    
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  if (isExpired) {
    return <span className="text-green-600 font-medium text-xs">Claiming Open!</span>;
  }
  
  // Badge variant - beautiful compact badges like admin panel
  if (variant === 'badge') {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-1 bg-[#f0b90b] text-white px-2 py-1 rounded-lg">
          <span className="text-sm font-bold">{String(timeLeft.days).padStart(2, '0')}</span>
          <span className="text-[10px] opacity-80">d</span>
        </div>
        <div className="flex items-center gap-1 bg-[#f0b90b] text-white px-2 py-1 rounded-lg">
          <span className="text-sm font-bold">{String(timeLeft.hours).padStart(2, '0')}</span>
          <span className="text-[10px] opacity-80">h</span>
        </div>
        <div className="flex items-center gap-1 bg-[#f0b90b] text-white px-2 py-1 rounded-lg">
          <span className="text-sm font-bold">{String(timeLeft.minutes).padStart(2, '0')}</span>
          <span className="text-[10px] opacity-80">m</span>
        </div>
        <div className="flex items-center gap-1 bg-[#f0b90b] text-white px-2 py-1 rounded-lg">
          <span className="text-sm font-bold">{String(timeLeft.seconds).padStart(2, '0')}</span>
          <span className="text-[10px] opacity-80">s</span>
        </div>
      </div>
    );
  }
  
  // Inline variant - ultra thin, elegant
  if (variant === 'inline') {
    return (
      <span className="font-mono text-[#1a1a1a] font-semibold tracking-wide">
        {timeLeft.days}<span className="text-[#f0b90b]">d</span>
        {' '}<span className="text-[#999]">:</span>{' '}
        {String(timeLeft.hours).padStart(2, '0')}<span className="text-[#f0b90b]">h</span>
        {' '}<span className="text-[#999]">:</span>{' '}
        {String(timeLeft.minutes).padStart(2, '0')}<span className="text-[#f0b90b]">m</span>
        {' '}<span className="text-[#999]">:</span>{' '}
        {String(timeLeft.seconds).padStart(2, '0')}<span className="text-[#f0b90b]">s</span>
      </span>
    );
  }
  
  // Default/claim variant - compact card style
  const colorClass = variant === 'claim' ? 'text-[#f0b90b]' : 'text-green-600';
  const bgClass = variant === 'claim' ? 'bg-[#f0b90b]/10' : 'bg-green-500/10';
  
  return (
    <div className={`rounded-lg px-3 py-2 ${bgClass}`}>
      <div className="flex items-center gap-2">
        <Timer className={`w-3.5 h-3.5 ${colorClass}`} />
        {label && <span className={`text-xs font-medium ${colorClass}`}>{label}</span>}
        <span className={`font-mono text-sm font-semibold ${colorClass}`}>
          {timeLeft.days}d : {String(timeLeft.hours).padStart(2, '0')}h : {String(timeLeft.minutes).padStart(2, '0')}m : {String(timeLeft.seconds).padStart(2, '0')}s
        </span>
      </div>
    </div>
  );
};

const ERC20_ABI = [
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
];

const DISTRIBUTOR_ABI = [
  {
    inputs: [
      { name: 'wallet', type: 'address' },
      { name: 'points', type: 'uint256' },
      { name: 'proof', type: 'bytes32[]' }
    ],
    name: 'claim',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  }
];

export const EmbedRewardsPage = () => {
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const publicClient = usePublicClient();
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingEpoch, setClaimingEpoch] = useState(null);
  const [epochs, setEpochs] = useState({});
  
  // Leaderboard state
  const [allEpochs, setAllEpochs] = useState([]);
  const [selectedEpochId, setSelectedEpochId] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [category, setCategory] = useState('overall');
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  
  // Treasury state
  const [treasuryBalance, setTreasuryBalance] = useState('0');
  const [tipPrice, setTipPrice] = useState(null);
  const [treasuryLoading, setTreasuryLoading] = useState(true);

  const { writeContract, data: hash, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash });

  // Fetch treasury balance and BNB price
  const fetchTreasuryData = async () => {
    try {
      setTreasuryLoading(true);
      
      // Fetch treasury BNB balance (treasury receives BNB from token swaps)
      if (publicClient) {
        try {
          const balance = await publicClient.getBalance({
            address: TREASURY_ADDRESS,
          });
          setTreasuryBalance(formatUnits(balance, 18));
        } catch (balanceError) {
          console.error('Error fetching treasury balance:', balanceError);
        }
      }
      
      // Fetch BNB price separately (always try, independent of balance fetch)
      try {
        const priceRes = await axios.get(`${BACKEND_URL}/api/bnb-price`);
        if (priceRes.data?.price) {
          setTipPrice(priceRes.data.price);
        } else {
          // Fallback BNB price if API returns no price
          setTipPrice(650);
        }
      } catch (e) {
        console.error('Error fetching BNB price:', e);
        // Fallback BNB price
        setTipPrice(650);
      }
      
    } catch (error) {
      console.error('Error fetching treasury data:', error);
    } finally {
      setTreasuryLoading(false);
    }
  };

  // Load epochs on mount (independent of wallet connection)
  useEffect(() => {
    loadAllEpochs();
  }, []);

  useEffect(() => {
    fetchTreasuryData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchTreasuryData, 30000);
    return () => clearInterval(interval);
  }, [publicClient]);
  
  // Load leaderboard when epoch or category changes
  useEffect(() => {
    if (selectedEpochId) {
      loadLeaderboard(selectedEpochId);
    }
  }, [selectedEpochId, category]);

  useEffect(() => {
    if (isConnected && address) {
      loadRewards();
    } else {
      setLoading(false);
    }
  }, [address, isConnected]);

  useEffect(() => {
    if (isSuccess && claimingEpoch) {
      handleClaimSuccess();
    }
  }, [isSuccess]);
  
  const loadAllEpochs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/epochs`);
      const epochsList = res.data.epochs || [];
      setAllEpochs(epochsList);
      
      // Select the most recent epoch by default
      if (epochsList.length > 0) {
        setSelectedEpochId(epochsList[0].epochId);
      }
    } catch (error) {
      console.error('Error loading epochs:', error);
    }
  };
  
  const loadLeaderboard = async (epochId) => {
    try {
      setLeaderboardLoading(true);
      const res = await axios.get(
        `${BACKEND_URL}/api/leaderboard/epoch/${epochId}?category=${category}`
      );
      setLeaderboard(res.data.leaderboard || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setLeaderboard([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const loadRewards = async () => {
    try {
      setLoading(true);
      const [rewardsRes, epochsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/rewards/${address}`),
        axios.get(`${BACKEND_URL}/api/epochs`)
      ]);
      
      setRewards(rewardsRes.data);
      
      const epochMap = {};
      epochsRes.data.epochs?.forEach(e => {
        epochMap[e.epochId] = e;
      });
      setEpochs(epochMap);
    } catch (error) {
      console.error('Error loading rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (epochId) => {
    try {
      const proofRes = await axios.get(`${BACKEND_URL}/api/proof/${epochId}/${address}`);
      const { amount, proof } = proofRes.data;
      
      const epoch = epochs[epochId];
      if (!epoch?.distributorAddress) {
        toast.error('Distributor not deployed yet');
        return;
      }

      setClaimingEpoch(epochId);
      
      // Amount is already in wei format from the API
      const amountBigInt = BigInt(amount);
      
      writeContract({
        address: epoch.distributorAddress,
        abi: DISTRIBUTOR_ABI,
        functionName: 'claim',
        args: [address, amountBigInt, proof]
      });
      
      toast.info('Please confirm in your wallet...');
    } catch (error) {
      console.error('Claim error:', error);
      toast.error(error.response?.data?.detail || 'Failed to claim');
      setClaimingEpoch(null);
    }
  };

  const handleClaimSuccess = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/claim/update`, {
        epochId: claimingEpoch,
        wallet: address,
        tx_hash: hash
      });
      // Handle pending receipt case
      if (response.data.status === 'pending') {
        toast.info('Claim confirmed on-chain. Syncing...');
      } else {
        toast.success('Rewards claimed successfully!');
      }
      loadRewards();
    } catch (error) {
      console.error('Error updating claim:', error);
      toast.success('Rewards claimed! (Refresh to update status)');
    } finally {
      setClaimingEpoch(null);
    }
  };

  // Not Connected State
  if (!isConnected) {
    const treasuryUSD = tipPrice ? (parseFloat(treasuryBalance) * tipPrice) : null;
    
    // Find active epoch for countdown
    const activeEpoch = allEpochs.find(e => e.status === 'COLLECTING' || e.status === 'ACTIVE');
    
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-6">
        <div className="max-w-xl mx-auto">
          
          {/* Header Section */}
          <div className="text-center mb-5">
            <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-3 py-1.5 mb-3">
              <span className="w-2 h-2 bg-[#f0b90b] rounded-full"></span>
              <span className="text-xs font-medium text-[#333]">REWARDS</span>
            </div>
            <h1 className="text-2xl font-bold mb-2 text-[#1a1a1a]">TipMe Rewards</h1>
            <p className="text-[#666] text-sm mb-3">
              Earn rewards for tipping and creating. A portion of each transaction fuels the ecosystem.
            </p>
            
            {/* Claiming Opens Countdown */}
            {activeEpoch?.endTime && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2 text-xs text-[#666]">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Claiming opens in</span>
                </div>
                <CountdownTimer targetDate={activeEpoch.endTime} variant="badge" />
              </div>
            )}
          </div>
          
          {/* Treasury Balance - Compact */}
          <Card className="p-4 bg-[#f0b90b] text-white border-0 shadow-lg rounded-2xl mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Coins className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white/90 text-sm font-medium">Community Rewards Pool</p>
                  <p className="text-xs text-white/60">Live Treasury Balance</p>
                </div>
              </div>
              <div className="text-right">
                {treasuryLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <p className="text-2xl font-bold">{formatNumber(parseFloat(treasuryBalance))} <span className="text-sm opacity-80">BNB</span></p>
                    {treasuryUSD !== null && (
                      <p className="text-sm text-white/80">≈ ${treasuryUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card>
          
          {/* Connect Card - Compact */}
          <Card className="p-4 bg-[#e8e3db] border-0 shadow-lg rounded-2xl mb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#d4cfc4] rounded-xl flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-[#f0b90b]" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#1a1a1a]">Connect Your Wallet</h2>
                  <p className="text-[#666] text-xs">View and claim your rewards</p>
                </div>
              </div>
              <Button 
                onClick={openConnectModal}
                className="h-9 px-5 bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold text-sm rounded-xl shadow-md transition-all"
              >
                Connect
              </Button>
            </div>
          </Card>
          
          {/* How Rewards Work - Slightly Compact */}
          <Card className="p-5 bg-[#e8e3db] border-0 shadow-lg rounded-2xl mb-4">
            <h3 className="text-lg font-bold mb-4 text-[#1a1a1a]">How Rewards Work</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#d4cfc4] rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 text-[#f0b90b]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1a1a1a] text-sm">1. Earn Points</p>
                  <p className="text-[#666] text-xs">Tip using <strong className="text-[#f0b90b]">$TIP</strong> to earn TRUE Points. Only creators need to verify!</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#d4cfc4] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-[#f0b90b]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1a1a1a] text-sm">2. Weekly Epochs</p>
                  <p className="text-[#666] text-xs">Rewards are calculated at the end of each week based on activity</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#d4cfc4] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-4 h-4 text-[#f0b90b]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1a1a1a] text-sm">3. Claim BNB</p>
                  <p className="text-[#666] text-xs">Claim your BNB rewards directly to your wallet</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Info Box - Compact */}
          <Card className="p-4 bg-[#e8e3db] border-0 shadow-lg rounded-2xl mb-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-[#666] text-xs">{Math.round(TIPPER_WEIGHT * 100)}% to tippers, {Math.round(CREATOR_WEIGHT * 100)}% to creators</span>
            </div>
            <div className="flex items-center gap-3 mt-1.5">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-[#666] text-xs">5-day claim window per epoch</span>
            </div>
          </Card>

          {/* T.R.U.E. Model Section - Dark Card (Not Connected) */}
          <Card className="p-5 mt-4 bg-[#1a1a1a] border-0 shadow-lg rounded-2xl text-white">
            <div className="mb-4">
              <h2 className="text-xl font-bold mb-1">
                <span className="text-[#f0b90b]">T.R.U.E.</span> Model
              </h2>
              <p className="text-xs text-[#888]">
                Tipping Rewards for User Engagement
              </p>
            </div>
            
            {/* Your Rewards */}
            <div className="bg-[#2a2a2a] rounded-xl p-4 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-[#f0b90b]" />
                <span className="text-xs text-[#888]">Your Rewards</span>
              </div>
              <div className="text-2xl font-bold text-[#f0b90b] mb-1">
                0.000000 BNB
              </div>
              <div className="flex gap-3 text-xs">
                <span className="text-green-400">✓ 0.000000 claimed</span>
                <span className="text-[#f0b90b]">○ 0.000000 pending</span>
              </div>
            </div>
            
            {/* Activity */}
            <div className="bg-[#2a2a2a] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#f0b90b]" />
                <span className="text-xs text-[#888]">Activity</span>
              </div>
              <div className="text-base font-medium">
                0 epochs participated
              </div>
            </div>
          </Card>
          
          {/* Leaderboard Section */}
          <Card className="p-6 mt-6 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#1a1a1a]">Leaderboard</h2>
              {allEpochs.length > 0 && allEpochs[0] && (allEpochs[0].status === 'COLLECTING' || allEpochs[0].status === 'ACTIVE') && (
                <div className="flex items-center gap-1 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                  <Sparkles className="w-3 h-3" />
                  <span>LIVE</span>
                </div>
              )}
            </div>
            
            {/* Weighting explanation */}
            <p className="text-xs text-[#888] mb-2">
              Points are weighted: {Math.round(TIPPER_WEIGHT * 100)}% tipper activity, {Math.round(CREATOR_WEIGHT * 100)}% creator activity
            </p>
            {/* Merkle distribution note for finalized epochs */}
            {selectedEpochId && allEpochs.find(e => e.epochId === selectedEpochId)?.status !== 'COLLECTING' && (
              <p className="text-xs text-[#666] mb-4 italic">
                Claimable amounts are based on the published Merkle distribution for this epoch.
              </p>
            )}
            {/* Epoch Tabs */}
            {allEpochs.length > 0 && (
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                {allEpochs.map((epoch) => {
                  const isActive = epoch.status === 'COLLECTING' || epoch.status === 'ACTIVE';
                  const isSelected = epoch.epochId === selectedEpochId;
                  return (
                    <button
                      key={epoch.epochId}
                      onClick={() => setSelectedEpochId(epoch.epochId)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                        isSelected
                          ? 'bg-[#1a1a1a] text-white shadow-md'
                          : 'bg-[#d4cfc4] text-[#666] hover:bg-[#c8c3bb] hover:text-[#333]'
                      }`}
                    >
                      {isActive && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>}
                      Epoch {epoch.epochId}
                      {isActive && <span className="text-xs opacity-70">Active</span>}
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Category Tabs */}
            <div className="flex gap-1 p-1 bg-[#d4cfc4] rounded-xl mb-4">
              {['overall', 'tippers', 'creators'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all capitalize ${
                    category === cat
                      ? 'bg-white text-[#1a1a1a] shadow-sm'
                      : 'text-[#666] hover:text-[#333]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            
            {/* Leaderboard List */}
            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
              {leaderboardLoading ? (
                <div className="text-center py-8 text-[#888]">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#f0b90b]" />
                  Loading leaderboard...
                </div>
              ) : (
                <>
                  {leaderboard.slice(0, 20).map((entry, index) => (
                    <div
                      key={entry.wallet}
                      className="rounded-xl p-3 flex items-center gap-3 bg-[#d4cfc4]/50 hover:bg-[#d4cfc4]"
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-[#f0b90b] text-white' :
                        index === 1 ? 'bg-[#c0c0c0] text-white' :
                        index === 2 ? 'bg-[#cd7f32] text-white' :
                        'bg-[#e0dbd3] text-[#666]'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-[#333] truncate">
                          {formatAddress(entry.wallet)}
                        </div>
                        <div className="text-xs text-[#888]">
                          {entry.tipsSent || 0} tips sent · {entry.tipsReceived || 0} received
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[#1a1a1a]">
                          {formatNumber(
                            category === 'creators' ? parseFloat(entry.creatorPoints || 0) :
                            category === 'tippers' ? parseFloat(entry.tipperPoints || 0) :
                            parseFloat(entry.totalTruePoints || 0)
                          )}
                        </div>
                        <div className="text-xs text-[#888]">TRUE pts</div>
                      </div>
                    </div>
                  ))}
                  
                  {leaderboard.length === 0 && (
                    <div className="text-center py-8 text-[#888]">
                      No tips recorded yet for this epoch.
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

        </div>
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-8 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#f0b90b] mx-auto mb-4" />
          <p className="text-[#666]">Loading rewards...</p>
        </div>
      </div>
    );
  }

  const unclaimedEpochs = rewards?.epochs?.filter(e => !e.claimed && parseFloat(e.totalReward) > 0) || [];
  const claimedEpochs = rewards?.epochs?.filter(e => e.claimed) || [];
  const treasuryUSD = tipPrice ? (parseFloat(treasuryBalance) * tipPrice) : null;
  
  // Find active epoch for countdown (connected state)
  const activeEpoch = allEpochs.find(e => e.status === 'COLLECTING' || e.status === 'ACTIVE');

  return (
    <div className="min-h-screen bg-[#c8c3bb] p-8">
      <div className="max-w-xl mx-auto">
        
        {/* Treasury Balance - Compact */}
        <Card className="p-4 bg-[#f0b90b] text-white border-0 shadow-lg rounded-2xl mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Coins className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white/90 text-sm font-medium">Community Rewards Pool</p>
                <p className="text-xs text-white/60">Live Treasury Balance</p>
              </div>
            </div>
            <div className="text-right">
              {treasuryLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <p className="text-2xl font-bold">{formatNumber(parseFloat(treasuryBalance))} <span className="text-sm opacity-80">BNB</span></p>
                  {treasuryUSD !== null && (
                    <p className="text-sm text-white/80">≈ ${treasuryUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#f0b90b] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2">Your Rewards</h1>
          <p className="text-[#666] mb-4">
            <span className="font-mono bg-[#d4cfc4] px-2 py-1 rounded text-sm">{address?.slice(0, 6)}...{address?.slice(-4)}</span>
          </p>
          
          {/* Claiming Opens Countdown - Connected State */}
          {activeEpoch?.endTime && (
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-xs text-[#666]">
                <Clock className="w-3.5 h-3.5" />
                <span>Claiming opens in</span>
              </div>
              <CountdownTimer targetDate={activeEpoch.endTime} variant="badge" />
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="p-5 bg-[#e8e3db] border-0 shadow-lg rounded-2xl text-center">
            <p className="text-xs text-[#888] mb-1 uppercase tracking-wide">Total Earned</p>
            <p className="text-xl font-bold text-[#f0b90b]">{parseFloat(rewards?.totalEarned || 0).toFixed(6)}</p>
            <p className="text-xs text-[#888]">BNB</p>
          </Card>
          <Card className="p-5 bg-[#e8e3db] border-0 shadow-lg rounded-2xl text-center">
            <p className="text-xs text-[#888] mb-1 uppercase tracking-wide">Claimed</p>
            <p className="text-xl font-bold text-green-600">{parseFloat(rewards?.totalClaimed || 0).toFixed(6)}</p>
            <p className="text-xs text-[#888]">BNB</p>
          </Card>
          <Card className="p-5 bg-[#e8e3db] border-0 shadow-lg rounded-2xl text-center">
            <p className="text-xs text-[#888] mb-1 uppercase tracking-wide">Unclaimed</p>
            <p className="text-xl font-bold text-[#f0b90b]">{parseFloat(rewards?.totalUnclaimed || 0).toFixed(6)}</p>
            <p className="text-xs text-[#888]">BNB</p>
          </Card>
        </div>

        {/* Unclaimed Rewards */}
        {unclaimedEpochs.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4 text-[#1a1a1a]">Claimable Rewards</h3>
            <div className="space-y-4">
              {unclaimedEpochs.map(epoch => {
                const epochData = epochs[epoch.epochId];
                const isFunded = epoch.isFunded === true;
                const canClaim = epochData?.status === 'CLAIMABLE' && epochData?.distributorAddress && isFunded;
                const isClaiming = claimingEpoch === epoch.epochId && (isPending || isConfirming);
                const claimDeadline = epochData?.claimDeadline;
                
                return (
                  <Card key={epoch.epochId} className={`p-6 border-2 shadow-lg rounded-2xl ${isFunded ? 'bg-[#f0b90b]/10 border-[#f0b90b]/30' : 'bg-gray-100 border-gray-300'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm text-[#888] mb-1">Epoch #{epoch.epochId}</p>
                        <p className={`text-3xl font-bold ${isFunded ? 'text-[#f0b90b]' : 'text-gray-400'}`}>
                          {parseFloat(epoch.totalReward || 0).toFixed(6)} <span className="text-lg">BNB</span>
                        </p>
                      </div>
                      <Button
                        onClick={() => handleClaim(epoch.epochId)}
                        disabled={!canClaim || isClaiming}
                        className={`h-12 px-8 font-semibold rounded-xl shadow-md ${
                          isFunded 
                            ? 'bg-[#f0b90b] hover:bg-[#d4a50a] text-white' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {isClaiming ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Claiming...</>
                        ) : isFunded ? (
                          'Claim'
                        ) : (
                          'Awaiting Funding'
                        )}
                      </Button>
                    </div>
                    {/* Not Funded Warning */}
                    {!isFunded && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3">
                        <p className="text-sm text-amber-700">
                          ⚠️ Distributor not yet funded. Claims will be enabled once the reward pool is funded.
                        </p>
                      </div>
                    )}
                    {/* Claim Window Countdown */}
                    {claimDeadline && canClaim && (
                      <CountdownTimer targetDate={claimDeadline} label="Claim window closes in" variant="claim" />
                    )}
                    {!canClaim && !isClaiming && isFunded && (
                      <p className="text-xs text-[#888] mt-3">Waiting for epoch to become claimable</p>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Claimed History */}
        {claimedEpochs.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4 text-[#1a1a1a]">Claimed Rewards</h3>
            <Card className="bg-[#e8e3db] border-0 shadow-lg rounded-2xl overflow-hidden">
              {claimedEpochs.map((epoch, idx) => (
                <div 
                  key={epoch.epochId} 
                  className={`p-4 flex items-center justify-between ${idx !== claimedEpochs.length - 1 ? 'border-b border-[#d4cfc4]' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                    <span className="text-[#666]">Epoch #{epoch.epochId}</span>
                  </div>
                  <span className="font-semibold text-[#1a1a1a]">{parseFloat(epoch.totalReward || 0).toFixed(6)} BNB</span>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* No Rewards */}
        {unclaimedEpochs.length === 0 && claimedEpochs.length === 0 && (
          <Card className="p-10 text-center bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
            <div className="w-16 h-16 bg-[#d4cfc4] rounded-2xl flex items-center justify-center mx-auto mb-5">
              <AlertCircle className="w-8 h-8 text-[#888]" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-[#1a1a1a]">No Rewards Yet</h3>
            <p className="text-[#666]">Start tipping to earn TRUE Points and unlock rewards</p>
          </Card>
        )}

        {/* Info */}
        <Card className="p-5 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-[#666] text-sm">Merkle proof verified claims</span>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-[#666] text-sm">5-day claim window per epoch</span>
          </div>
        </Card>
        
        {/* T.R.U.E. Model Section - Dark Card */}
        <Card className="p-6 mt-6 bg-[#1a1a1a] border-0 shadow-lg rounded-2xl text-white">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">
              <span className="text-[#f0b90b]">T.R.U.E.</span> Model
            </h2>
            <p className="text-sm text-[#888]">
              Tipping Rewards for User Engagement
            </p>
          </div>
          
          {/* Your Rewards */}
          <div className="bg-[#2a2a2a] rounded-xl p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-[#f0b90b]" />
              <span className="text-sm text-[#888]">Your Rewards</span>
            </div>
            <div className="text-3xl font-bold text-[#f0b90b] mb-2">
              {parseFloat(rewards?.totalEarned || 0).toFixed(6)} BNB
            </div>
            <div className="flex gap-4 text-sm">
              <span className="text-green-400">
                ✓ {parseFloat(rewards?.totalClaimed || 0).toFixed(6)} claimed
              </span>
              <span className="text-[#f0b90b]">
                ○ {parseFloat(rewards?.totalUnclaimed || 0).toFixed(6)} pending
              </span>
            </div>
          </div>
          
          {/* Activity */}
          <div className="bg-[#2a2a2a] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-[#f0b90b]" />
              <span className="text-sm text-[#888]">Activity</span>
            </div>
            <div className="text-lg font-medium">
              {rewards?.epochs?.length || 0} epochs participated
            </div>
          </div>
        </Card>
        
        {/* Leaderboard Section */}
        <Card className="p-6 mt-6 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1a1a1a]">Leaderboard</h2>
            {allEpochs.length > 0 && allEpochs[0] && (allEpochs[0].status === 'COLLECTING' || allEpochs[0].status === 'ACTIVE') && (
              <div className="flex items-center gap-1 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                <Sparkles className="w-3 h-3" />
                <span>LIVE</span>
              </div>
            )}
          </div>
          
          {/* Epoch Tabs */}
          {allEpochs.length > 0 && (
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {allEpochs.map((epoch) => {
                const isActive = epoch.status === 'COLLECTING' || epoch.status === 'ACTIVE';
                const isSelected = epoch.epochId === selectedEpochId;
                return (
                  <button
                    key={epoch.epochId}
                    onClick={() => setSelectedEpochId(epoch.epochId)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                      isSelected
                        ? 'bg-[#1a1a1a] text-white shadow-md'
                        : 'bg-[#d4cfc4] text-[#666] hover:bg-[#c8c3bb] hover:text-[#333]'
                    }`}
                  >
                    {isActive && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>}
                    Epoch {epoch.epochId}
                    {isActive && <span className="text-xs opacity-70">Active</span>}
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Category Tabs */}
          <div className="flex gap-1 p-1 bg-[#d4cfc4] rounded-xl mb-4">
            {['overall', 'tippers', 'creators'].map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all capitalize ${
                  category === cat
                    ? 'bg-white text-[#1a1a1a] shadow-sm'
                    : 'text-[#666] hover:text-[#333]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          
          {/* Leaderboard List */}
          <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
            {leaderboardLoading ? (
              <div className="text-center py-8 text-[#888]">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#f0b90b]" />
                Loading leaderboard...
              </div>
            ) : (
              <>
                {leaderboard.slice(0, 20).map((entry, index) => {
                  const isCurrentUser = entry.wallet?.toLowerCase() === address?.toLowerCase();
                  return (
                    <div
                      key={entry.wallet}
                      className={`rounded-xl p-3 flex items-center gap-3 ${
                        isCurrentUser 
                          ? 'bg-[#f0b90b]/20 border-2 border-[#f0b90b]/50' 
                          : 'bg-[#d4cfc4]/50 hover:bg-[#d4cfc4]'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-[#f0b90b] text-white' :
                        index === 1 ? 'bg-[#c0c0c0] text-white' :
                        index === 2 ? 'bg-[#cd7f32] text-white' :
                        'bg-[#e0dbd3] text-[#666]'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-[#333] truncate flex items-center gap-2">
                          {formatAddress(entry.wallet)}
                          {isCurrentUser && <span className="text-xs bg-[#f0b90b] text-white px-2 py-0.5 rounded-full">You</span>}
                        </div>
                        <div className="text-xs text-[#888]">
                          {entry.tipsSent || 0} tips sent · {entry.tipsReceived || 0} received
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[#1a1a1a]">
                          {formatNumber(
                            category === 'creators' ? parseFloat(entry.creatorPoints || 0) :
                            category === 'tippers' ? parseFloat(entry.tipperPoints || 0) :
                            parseFloat(entry.totalTruePoints || 0)
                          )}
                        </div>
                        <div className="text-xs text-[#888]">TRUE pts</div>
                      </div>
                    </div>
                  );
                })}
                
                {leaderboard.length === 0 && (
                  <div className="text-center py-8 text-[#888]">
                    No tips recorded yet for this epoch.
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

      </div>
    </div>
  );
};
